import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import { navigationConfig } from '@config/navigation'
import { useAuthStore } from '@stores/authStore'
import { useThemeStore } from '@stores/themeStore'
// Import custom Flutter SVG icons as React components
import { LogoutIcon, LightIcon, DarkIcon, AdminIcon } from '../icons'

interface SidebarProps {
  state: 'expanded' | 'collapsed' | 'hidden'
  isVisible: boolean
  isMobileMenuOpen: boolean
  onMobileClose: () => void
}

export const Sidebar = ({ state, isMobileMenuOpen, onMobileClose }: SidebarProps) => {
  const isExpanded = state === 'expanded'
  const isHidden = state === 'hidden'
  const { user, logout } = useAuthStore()
  const { theme, toggleTheme } = useThemeStore()
  const isDark = theme === 'dark'
  const location = useLocation()
  const navigate = useNavigate()
  
  // Get navigation items based on user role
  const isAdmin = user?.role === 'admin'
  const navigation = navigationConfig.mainNavigation.filter(item => item.isVisible)
  
  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  function renderSidebarContent(showLabels: boolean = isExpanded) {
    return (
      <>
        {/* Header */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
          {showLabels && (
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">SkeenFit BO</h1>
          )}
          {/* Close button for mobile */}
          {isHidden && (
            <button
              onClick={onMobileClose}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700"
              aria-label="Fermer le menu"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        {/* Main Navigation */}
        <nav className="mt-6 px-4 flex-1">
          <ul className="space-y-1">
            {navigation.map((item) => {
              const IconComponent = item.icon
              return (
                <li key={item.id}>
                  <NavLink
                    to={item.path}
                    onClick={isHidden ? onMobileClose : undefined}
                    className={({ isActive }) => {
                      // Check if current route matches the navigation item
                      // For products and ingredients, also check child routes
                      const isCurrentActive = isActive || 
                        (item.id === 'products' && location.pathname.startsWith('/products')) ||
                        (item.id === 'ingredients' && location.pathname.startsWith('/ingredients'))
                      
                      return clsx(
                        'group flex items-center px-3 py-2 text-base font-normal rounded-md transition-all duration-200',
                        isCurrentActive
                          ? 'bg-sk-gold/20 text-sk-black dark:bg-sk-gold/10 dark:text-sk-gold'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                      )
                    }}
                    title={!showLabels ? item.label : undefined}
                  >
                    <IconComponent
                      className="flex-shrink-0 h-5 w-5"
                      aria-hidden="true"
                    />
                    {showLabels && (
                      <span className="ml-3">{item.label}</span>
                    )}
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* Action Buttons */}
        <div className="absolute bottom-5 left-0 right-0 px-4 space-y-2">
          {/* Admin Button */}
          {isAdmin && (
            <button
              onClick={() => {
                navigate('/admins')
                if (isHidden) onMobileClose()
              }}
              className={clsx(
                'w-full flex items-center px-3 py-2 text-base font-normal rounded-md transition-colors',
                'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white'
              )}
              title={!showLabels ? 'Admin' : undefined}
            >
              <AdminIcon className="flex-shrink-0 h-5 w-5" />
              {showLabels && <span className="ml-3">Admin</span>}
            </button>
          )}
          
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={clsx(
              'w-full flex items-center px-3 py-2 text-base font-normal rounded-md transition-colors',
              'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white'
            )}
            title={!showLabels ? (isDark ? 'Mode clair' : 'Mode sombre') : undefined}
          >
            {isDark ? (
              <LightIcon className="flex-shrink-0 h-5 w-5" />
            ) : (
              <DarkIcon className="flex-shrink-0 h-5 w-5" />
            )}
            {showLabels && (
              <span className="ml-3">{isDark ? 'Clair' : 'Sombre'}</span>
            )}
          </button>
          
          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className={clsx(
              'w-full flex items-center px-3 py-2 text-base font-normal rounded-md transition-colors',
              'text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/10 dark:hover:text-red-300'
            )}
            title={!showLabels ? 'Quitter' : undefined}
          >
            <LogoutIcon className="flex-shrink-0 h-5 w-5" />
            {showLabels && <span className="ml-3">Quitter</span>}
          </button>
        </div>
      </>
    )
  }

  // Mobile sidebar (overlay)
  if (isHidden) {
    return (
      <>
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-gray-900 shadow-lg">
              {renderSidebarContent(true)}
            </div>
          </div>
        )}
      </>
    )
  }

  // Desktop/Tablet sidebar
  return (
    <div className={clsx(
      'fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-900 shadow-lg transition-all duration-300',
      isExpanded ? 'w-64' : 'w-16'
    )}>
      {renderSidebarContent()}
    </div>
  )
}